import { hasEuLocations } from "@/lib/taxonomy";
import type { ListingStatus } from "@/lib/marketplace/listing-types";

export type { ListingStatus } from "@/lib/marketplace/listing-types";
export type PlacementStatus = "requested" | "confirmed" | "declined" | null;

export type CandidateJobStateKey =
  | "placed"
  | "closed"
  | "interviewing"
  | "shortlisted"
  | "messaged"
  | "interested"
  | "saved"
  | "available";

export interface CandidateJobStateInput {
  saved: boolean;
  interested: boolean;
  hasConversation: boolean;
  isShortlisted: boolean;
  placementStatus: PlacementStatus;
  listingStatus: ListingStatus;
}

export interface CandidateJobState {
  key: CandidateJobStateKey;
  label: string;
  helper: string;
  rank: number;
}

export interface MatchListingInput {
  roles: string[];
  locations: string[];
  startDate: string | null;
  endDate: string | null;
}

export interface MatchCandidateWindow {
  positions: string[];
  preferredLocations: string[];
  availableFrom: string;
  availableTo: string;
}

export interface MatchRightToWorkEntry {
  region: string;
  status: "yes" | "sponsorship";
}

export interface BuildMatchExplanationsInput {
  listing: MatchListingInput;
  candidateWindows: MatchCandidateWindow[];
  candidateRightToWork: MatchRightToWorkEntry[] | null;
  labelLookup: (id: string) => string;
}

export interface PipelineCandidate extends CandidateJobStateInput {
  id: string;
  name: string;
}

export interface PipelineGroup {
  key: CandidateJobStateKey;
  label: string;
  helper: string;
  candidates: PipelineCandidate[];
}

const STATE_COPY: Record<CandidateJobStateKey, Omit<CandidateJobState, "key">> = {
  placed: {
    label: "Placed",
    helper: "Placement confirmed for this role.",
    rank: 100,
  },
  closed: {
    label: "Closed",
    helper: "This listing is no longer active.",
    rank: 90,
  },
  interviewing: {
    label: "Interviewing",
    helper: "A placement request or hiring discussion is in progress.",
    rank: 80,
  },
  shortlisted: {
    label: "Shortlisted",
    helper: "The employer has saved this candidate.",
    rank: 70,
  },
  messaged: {
    label: "Messaged",
    helper: "A conversation has started.",
    rank: 60,
  },
  interested: {
    label: "Interested",
    helper: "Interest has been registered with the employer.",
    rank: 50,
  },
  saved: {
    label: "Saved",
    helper: "Saved for later. Express interest when you are ready.",
    rank: 10,
  },
  available: {
    label: "Available",
    helper: "No marketplace action has been taken yet.",
    rank: 0,
  },
};

export function deriveCandidateJobState(
  input: CandidateJobStateInput
): CandidateJobState {
  if (input.placementStatus === "confirmed") {
    return state("placed");
  }

  if (input.listingStatus !== "active") {
    return state("closed");
  }

  if (input.placementStatus === "requested") {
    return state("interviewing");
  }

  if (input.isShortlisted) {
    return state("shortlisted");
  }

  if (input.hasConversation) {
    return state("messaged");
  }

  if (input.interested) {
    return state("interested");
  }

  if (input.saved) {
    return state("saved");
  }

  return state("available");
}

export function buildMatchExplanations({
  listing,
  candidateWindows,
  candidateRightToWork,
  labelLookup,
}: BuildMatchExplanationsInput): string[] {
  const reasons: string[] = [];

  const hasWindowDates = candidateWindows.length > 0;
  const allPositions = unique(candidateWindows.flatMap((w) => w.positions));
  const allLocations = unique(
    candidateWindows.flatMap((w) => w.preferredLocations)
  );

  if (!hasWindowDates) {
    reasons.push("Add availability dates to check the season fit.");
  } else if (!listing.startDate || !listing.endDate) {
    reasons.push("The employer has marked the dates as flexible.");
  } else if (candidateWindows.some((w) => datesOverlap(w, listing))) {
    reasons.push("Your availability overlaps these dates.");
  } else {
    reasons.push("Your availability does not overlap these dates yet.");
  }

  if (allPositions.length === 0) {
    reasons.push("Add preferred roles to check role fit.");
  } else {
    const roleMatches = allPositions.filter((role) =>
      listing.roles.includes(role)
    );
    if (roleMatches.length > 0) {
      reasons.push(`Role match: ${formatLabels(roleMatches, labelLookup)}.`);
    } else {
      reasons.push("Your preferred roles do not match this listing yet.");
    }
  }

  if (allLocations.length === 0) {
    reasons.push("Add preferred locations to check place fit.");
  } else {
    const locationMatches = allLocations.filter((location) =>
      listing.locations.includes(location)
    );
    if (locationMatches.length > 0) {
      reasons.push(
        `Location match: ${formatLabels(locationMatches, labelLookup)}.`
      );
    } else {
      reasons.push("Your preferred locations do not match this listing yet.");
    }
  }

  if (hasEuLocations(listing.locations)) {
    if (!candidateRightToWork || candidateRightToWork.length === 0) {
      reasons.push("Add right-to-work details to check visa fit.");
    } else if (
      candidateRightToWork.some(
        (entry) =>
          (entry.region === "EU / Schengen" || entry.region === "Switzerland") &&
          entry.status === "yes"
      )
    ) {
      reasons.push("Your profile shows right to work for this region.");
    } else if (
      candidateRightToWork.some(
        (entry) =>
          (entry.region === "EU / Schengen" || entry.region === "Switzerland") &&
          entry.status === "sponsorship"
      )
    ) {
      reasons.push("Your profile says you may need sponsorship for this region.");
    } else {
      reasons.push("Your right-to-work details may need checking for this region.");
    }
  }

  return reasons.slice(0, 4);
}

export function buildEmployerPipelineGroups(
  candidates: PipelineCandidate[]
): PipelineGroup[] {
  const byState = new Map<CandidateJobStateKey, PipelineCandidate[]>();

  for (const candidate of candidates) {
    const stateKey = deriveCandidateJobState(candidate).key;
    const existing = byState.get(stateKey) ?? [];
    existing.push(candidate);
    byState.set(stateKey, existing);
  }

  return Array.from(byState.entries())
    .map(([key, groupCandidates]) => ({
      key,
      label: STATE_COPY[key].label,
      helper: STATE_COPY[key].helper,
      candidates: groupCandidates,
    }))
    .sort((a, b) => STATE_COPY[b.key].rank - STATE_COPY[a.key].rank);
}

function state(key: CandidateJobStateKey): CandidateJobState {
  return { key, ...STATE_COPY[key] };
}

function datesOverlap(
  window: MatchCandidateWindow,
  listing: MatchListingInput
): boolean {
  if (!listing.startDate || !listing.endDate) return false;
  const windowStart = new Date(window.availableFrom);
  const windowEnd = new Date(window.availableTo);
  const listingStart = new Date(listing.startDate);
  const listingEnd = new Date(listing.endDate);
  return windowStart < listingEnd && listingStart < windowEnd;
}

function unique(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function formatLabels(
  ids: string[],
  labelLookup: (id: string) => string
): string {
  return ids.slice(0, 2).map(labelLookup).join(", ");
}
